import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PILLARS, CROSS_CUTTING } from "@/data/platform";
import { INDUSTRIES } from "@/data/industries";
import { PillarIcon } from "@/components/platform/PlatformPrimitives";

function scrollToContact() {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function PillarCard({ pillar, i, dark = false }) {
  const liveCount = pillar.products.filter((p) => p.status === "live").length;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
    >
      <Link
        to={`/platform/${pillar.slug}`}
        data-testid={`home-pillar-${pillar.slug}`}
        className={`group h-full flex flex-col p-6 md:p-7 rounded-2xl transition-all min-h-[260px] ${
          dark
            ? "bg-[#FDFCFB]/[0.04] border border-[#FDFCFB]/10 text-[#FDFCFB] hover:bg-[#FDFCFB]/[0.08]"
            : "bg-[#FDFCFB] border border-[#E8E6E1] hover:border-[#1A1A1A]/30 hover:-translate-y-1 hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.18)]"
        }`}
      >
        <div className="flex items-start justify-between mb-5">
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${
              dark
                ? "bg-[#FDFCFB]/10 text-[#FDFCFB] group-hover:bg-[#FDFCFB]/15"
                : "bg-[#F5F4F0] text-[#1A1A1A] group-hover:bg-[#1A1A1A] group-hover:text-[#FDFCFB]"
            }`}
          >
            <PillarIcon name={pillar.icon} />
          </div>
          <span
            className={`font-mono-tech text-[10px] tracking-[0.18em] ${
              dark ? "text-[#FDFCFB]/50" : "text-[#6B6A68]"
            }`}
          >
            / {String(i + 1).padStart(2, "0")}
          </span>
        </div>
        <h3 className="font-serif-display text-2xl md:text-[26px] tracking-tight">
          {pillar.name}
        </h3>
        <p
          className={`mt-2 font-serif-display italic text-sm ${
            dark ? "text-[#FDFCFB]/65" : "text-[#1A1A1A]/75"
          }`}
        >
          {pillar.tagline}
        </p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {pillar.products.slice(0, 4).map((p) => (
            <span
              key={p.slug}
              className={`px-2.5 py-1 rounded-full text-[11px] ${
                dark ? "bg-[#FDFCFB]/10 text-[#FDFCFB]/85" : "bg-[#F5F4F0] text-[#1A1A1A]"
              }`}
            >
              {p.name}
            </span>
          ))}
          {pillar.products.length > 4 && (
            <span
              className={`px-2.5 py-1 rounded-full text-[11px] ${
                dark ? "text-[#FDFCFB]/50" : "text-[#6B6A68]"
              }`}
            >
              +{pillar.products.length - 4} more
            </span>
          )}
        </div>
        <div
          className={`mt-auto pt-6 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase font-mono-tech ${
            dark ? "text-[#FDFCFB]/60" : "text-[#6B6A68]"
          }`}
        >
          <span>{liveCount > 0 ? `${liveCount} live · ${pillar.products.length} total` : `${pillar.products.length} products`}</span>
          <span className={`inline-flex items-center gap-1 ${dark ? "text-[#FDFCFB]/85" : "text-[#1A1A1A]/70"}`}>
            Explore <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

function IndustryCard({ industry, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
    >
      <Link
        to={`/industries/${industry.slug}`}
        data-testid={`home-industry-${industry.slug}`}
        className="group block rounded-2xl overflow-hidden border border-[#E8E6E1] bg-[#FDFCFB] hover:-translate-y-0.5 hover:shadow-[0_22px_40px_-18px_rgba(0,0,0,0.18)] transition-all"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F5F4F0]">
          <img
            src={industry.image}
            alt={industry.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          {industry.brand && (
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#FDFCFB]/85 backdrop-blur-sm font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#1A1A1A]">
              {industry.brand}
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif-display text-lg md:text-xl text-[#1A1A1A] tracking-tight">
            {industry.name}
          </h3>
          <p className="mt-1 text-xs text-[#6B6A68]">{industry.tagline}</p>
        </div>
      </Link>
    </motion.div>
  );
}

export default function WhatWeBuild() {
  return (
    <>
      {/* PLATFORM PILLARS */}
      <section
        id="what-we-build"
        data-testid="what-we-build"
        className="relative py-24 md:py-32 bg-[#F5F4F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
            <div>
              <div className="caption mb-6">Platform · Every department</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05] max-w-3xl">
                One platform. <span className="italic">Every department.</span>
              </h2>
            </div>
            <p className="max-w-md text-[#6B6A68] md:text-right">
              Eight functional pillars + two cross-cutting layers, each holding
              ready-to-use products built around how teams actually work.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {PILLARS.map((p, i) => (
              <PillarCard key={p.slug} pillar={p} i={i} />
            ))}
          </div>

          {/* Cross-cutting */}
          <div className="caption mt-16 mb-5">/ Cross-cutting</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CROSS_CUTTING.map((p, i) => (
              <PillarCard key={p.slug} pillar={p} i={i} />
            ))}
          </div>

          <div className="mt-14 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="text-sm text-[#6B6A68] max-w-xl">
              Don't see what you need? We've shipped from booking platforms to
              fractional-CTO engagements. Tell us the problem.
            </p>
            <Link
              to="/platform"
              data-testid="explore-platform"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333] transition-all"
            >
              See the full platform
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* INDUSTRY SUITES */}
      <section
        id="industries"
        data-testid="industries-section"
        className="relative py-24 md:py-32 bg-[#1A1A1A] text-[#FDFCFB]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
            <div>
              <div className="caption !text-[#FDFCFB]/50 mb-6">Industries · Pre-bundled suites</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl">
                Or — start by <span className="italic">industry.</span>
              </h2>
            </div>
            <p className="max-w-md text-[#FDFCFB]/65 md:text-right">
              Each suite bundles the right products across pillars for one
              vertical. Run the entire business on one workspace.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((ind, i) => (
              <IndustryCard key={ind.slug} industry={ind} i={i} />
            ))}
          </div>

          <div className="mt-14 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="text-sm text-[#FDFCFB]/60 max-w-xl">
              New industries added quarterly. If yours isn't here yet, we can
              build a bespoke suite for you.
            </p>
            <Link
              to="/industries"
              data-testid="explore-industries"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FDFCFB] text-[#1A1A1A] text-sm hover:bg-[#FDFCFB]/90 transition-all"
            >
              Browse all industries
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="mt-16 pt-10 border-t border-[#FDFCFB]/15 flex flex-wrap gap-3">
            <button
              onClick={scrollToContact}
              className="text-xs tracking-[0.18em] uppercase font-mono-tech text-[#FDFCFB]/60 hover:text-[#FDFCFB] underline underline-offset-4 decoration-[#FDFCFB]/30"
            >
              Talk to us instead →
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
